import React from "react";
import { COUNTRY_CODES } from "@/utils/countryCodes";
import { Search, ChevronDown } from "lucide-react";

interface PhoneCodePickerProps {
    value: string;
    onChange: (code: string) => void;
}

export function PhoneCodePicker({ value, onChange }: PhoneCodePickerProps) {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const ref = React.useRef<HTMLDivElement>(null);

    const selected = COUNTRY_CODES.find((c) => c.code === value) ?? COUNTRY_CODES.find((c) => c.iso === "TR")!;
    const filtered = COUNTRY_CODES.filter(
        (c) =>
            c.name.toLowerCase().includes(query.toLowerCase()) ||
            c.code.includes(query)
    );

    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div ref={ref} className="relative shrink-0">
            <button
                type="button"
                onClick={() => { setOpen((o) => !o); setQuery(""); }}
                className="flex items-center gap-1.5 h-12 w-full rounded-xl border bg-white px-3 text-xs font-black text-slate-700 hover:bg-slate-50 focus:outline-none cursor-pointer transition-colors border-slate-800"
            >
                <span>{selected.flag}</span>
                <span>{selected.code}</span>
                <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div className="absolute top-full left-0 z-50 mt-1 w-64 rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
                        <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <input
                            autoFocus
                            placeholder="Search country..."
                            className="flex-1 text-xs bg-transparent focus:outline-none placeholder:text-slate-400"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>
                    <ul className="max-h-36 overflow-y-auto">
                        {filtered.length === 0 && (
                            <li className="px-4 py-3 text-xs text-slate-400">No results</li>
                        )}
                        {filtered.map((c) => (
                            <li
                                key={c.iso}
                                className={`flex items-center gap-2.5 px-4 py-2.5 cursor-pointer hover:bg-slate-50 text-xs transition-colors ${
                                    c.code === selected.code && c.iso === selected.iso ? "bg-slate-100 font-black" : ""
                                }`}
                                onMouseDown={() => { onChange(c.code); setOpen(false); setQuery(""); }}
                            >
                                <span className="text-base leading-none">{c.flag}</span>
                                <span className="flex-1 font-medium text-slate-800">{c.name}</span>
                                <span className="font-black text-slate-500">{c.code}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
